import { MobModel } from "types";

import behemalPilotImage from "assets/mob-behemal-pilot.svg";
import behemalShipImage from "assets/mob-behemal-ship.svg";
import reaverPilotImage from "assets/mob-reaver-pilot.svg";
import reaverShipImage from "assets/mob-reaver-ship.svg";
import tracerPilotImage from "assets/mob-tracer-pilot.svg";
import tracerShipImage from "assets/mob-tracer-ship.svg";
import vexenPilotImage from "assets/mob-vexen-pilot.svg";
import vexenShipImage from "assets/mob-vexen-ship.svg";
import voltaPilotImage from "assets/mob-volta-pilot.svg";
import voltaShipImage from "assets/mob-volta-ship.svg";

import { BASE_STATS } from "data/baseStats";

const { FIREPOWER, RESILIENCE, SPEED, PRECISION, ENERGY } = BASE_STATS;

// SECTOR TIER 3 - MEDALLION RANGE: 81-180

const SENECA_UNDERHAVEN_MOBS: Record<string, MobModel> = {
  vexen: {
    name: "Vexen",
    images: {
      ship: vexenShipImage,
      pilot: vexenPilotImage,
    },
    dialogs: {
      opening:
        "I'm surprised you're here in Underhaven. We're pretty tough here.",
      win: "Well, I guess you're tough too. Welcome to Underhaven.",
      lose: "Maybe go back to Haven. You're not ready for UNDERhaven.",
    },
    position: 40,
    heightPercentage: 40,
    medallions: 90,
    baseStatsModifiers: {
      firepower: 2,
      resilience: 2,
      speed: 4,
      precision: 3,
      energy: 4,
    },
    baseStatsCosts: {
      firepower: 3,
      resilience: 2,
      speed: 2,
      precision: 2,
      energy: 1,
    },
    baseStatsTargets: [RESILIENCE, ENERGY, FIREPOWER, PRECISION, SPEED],
    isBoss: false,
    drops: {
      platinum: "burst_matrix",
      gold: "dash_matrix",
      silver: "nova_drive",
      bronze: "flux_scope",
    },
    installedUpgrades: {
      weapons: null,
      shields: null,
      thrusters: "nova_drive",
      targetingSystem: "flux_scope",
      reactorCore: "burst_matrix",
    },
    partPositions: {
      weapons: { x: 59, y: 58 },
      targeting: { x: 69, y: 58 },
      thrusters: { x: 30, y: 52.25 },
    },
    skills: ["shieldBypass", "rapidFire", "energyBlast"],
  },
  behemal: {
    name: "Behemal",
    images: {
      ship: behemalShipImage,
      pilot: behemalPilotImage,
    },
    dialogs: {
      opening: "Dude, I'm huge. You really wanna do this?",
      win: "Time to bulk up my ship again.",
      lose: "Do you even lift... antimatter into your ship?",
    },
    position: 80,
    heightPercentage: 40,
    medallions: 105,
    baseStatsModifiers: {
      firepower: 3,
      resilience: 4,
      speed: 2,
      precision: 2,
      energy: 4,
    },
    baseStatsCosts: {
      firepower: 2,
      resilience: 2,
      speed: 3,
      precision: 2,
      energy: 1,
    },
    baseStatsTargets: [PRECISION, ENERGY, FIREPOWER, RESILIENCE, SPEED],
    isBoss: false,
    drops: {
      platinum: "aegis_core",
      gold: "ward_nexus",
      silver: "null_barrier",
      bronze: "null_blade",
    },
    installedUpgrades: {
      weapons: "null_blade",
      shields: "null_barrier",
      thrusters: null,
      targetingSystem: null,
      reactorCore: "aegis_core",
    },
    partPositions: {
      weapons: { x: 68.25, y: 66 },
      targeting: { x: 78.25, y: 66 },
      thrusters: { x: 16, y: 56 },
    },
    skills: ["energyBlast", "emergencyRepairs", "shieldStorm"],
  },
  tracer: {
    name: "Tracer",
    images: {
      ship: tracerShipImage,
      pilot: tracerPilotImage,
    },
    dialogs: {
      opening: "I've been told I'm a very accurate shot.",
      win: "They were lying to me!?",
      lose: "More people should tell me I'm accurate. I like compliments.",
    },
    position: 120,
    heightPercentage: 40,
    medallions: 125,
    baseStatsModifiers: {
      firepower: 2,
      resilience: 2,
      speed: 3,
      precision: 4,
      energy: 4,
    },
    baseStatsCosts: {
      firepower: 2,
      resilience: 3,
      speed: 2,
      precision: 2,
      energy: 1,
    },
    baseStatsTargets: [FIREPOWER, ENERGY, RESILIENCE, PRECISION, SPEED],
    isBoss: false,
    drops: {
      platinum: "tracer_matrix",
      gold: "sight_matrix",
      silver: "phaser_sight",
      bronze: "plasma_drive",
    },
    installedUpgrades: {
      weapons: null,
      shields: null,
      thrusters: "plasma_drive",
      targetingSystem: "phaser_sight",
      reactorCore: "tracer_matrix",
    },
    partPositions: {
      weapons: { x: 69.75, y: 58.85 },
      targeting: { x: 79.75, y: 58.85 },
      thrusters: { x: 19.25, y: 51.5 },
    },
    skills: ["eagleEye", "pressurePoint", "energyBlast"],
  },
  volta: {
    name: "Volta",
    images: {
      ship: voltaShipImage,
      pilot: voltaPilotImage,
    },
    dialogs: {
      opening: "Firepower and energy is the way to go. My two passions.",
      win: "I have no passions now.",
      lose: `I'm going to coin the term "firepowergy". Do you like it?`,
    },
    position: 160,
    heightPercentage: 40,
    medallions: 150,
    baseStatsModifiers: {
      firepower: 4,
      resilience: 3,
      speed: 2,
      precision: 2,
      energy: 4,
    },
    baseStatsCosts: {
      firepower: 2,
      resilience: 2,
      speed: 2,
      precision: 3,
      energy: 1,
    },
    baseStatsTargets: [SPEED, ENERGY, RESILIENCE, PRECISION, FIREPOWER],
    isBoss: false,
    drops: {
      platinum: "ravager_core",
      gold: "reaper_core",
      silver: "void_blade",
      bronze: "null_shield",
    },
    installedUpgrades: {
      weapons: "void_blade",
      shields: "null_shield",
      thrusters: null,
      targetingSystem: null,
      reactorCore: "ravager_core",
    },
    partPositions: {
      weapons: { x: 60, y: 65 },
      targeting: { x: 70, y: 65 },
      thrusters: { x: 15, y: 58.5 },
    },
    skills: ["shieldBreaker", "energyBlast", "reactorOverload"],
  },
  reaver: {
    name: "Reaver",
    images: {
      ship: reaverShipImage,
      pilot: reaverPilotImage,
    },
    dialogs: {
      opening:
        "Antimatter is pretty cool. I tried eating it once, it didn't go well.",
      win: "Well dang, I lost. Maybe I should've eaten more antimatter.",
      lose: "Aha, I win! Maybe I should eat more antimatter.",
    },
    position: 200,
    heightPercentage: 40,
    medallions: 180,
    baseStatsModifiers: {
      firepower: 1,
      resilience: 2,
      speed: 4,
      precision: 3,
      energy: 5,
    },
    baseStatsCosts: {
      firepower: 3,
      resilience: 2,
      speed: 2,
      precision: 2,
      energy: 1,
    },
    baseStatsTargets: [ENERGY, PRECISION, FIREPOWER, RESILIENCE, SPEED],
    isBoss: false,
    drops: {
      platinum: "omega_core",
      gold: "apex_core",
      silver: "eclipse_core",
      bronze: "nebula_core",
    },
    installedUpgrades: {
      weapons: null,
      shields: null,
      thrusters: "nova_drive",
      targetingSystem: "phaser_sight",
      reactorCore: "omega_core",
    },
    partPositions: {
      weapons: { x: 63.75, y: 67.6 },
      targeting: { x: 73.75, y: 67.6 },
      thrusters: { x: 25.5, y: 60.75 },
    },
    skills: ["eagleEye", "emergencyRepairs", "energyBlast", "pressurePoint"],
  },
};

export default SENECA_UNDERHAVEN_MOBS;
